import { defineStore } from 'pinia'
import { ref } from 'vue'
import loanService from '@/services/loan.service'
import portalModeService, { type CompanyPortalChannels } from '@/services/portalmode.service'

export const usePortalModeStore = defineStore('portalMode', () => {
  const currentSelection = ref('broker')
  const isFetchingPortalMode = ref(false)
  const hasMultiplePortalModes = ref(false)
  const isNonDelPortal = ref(false)
  const isCorPortal = ref(false)
  const isBrokerPortal = ref(false)
  const availableChannels = ref([
    {
      key: 'hasBroker',
      name: 'Wholesale',
      value: 'broker',
      icon: 'fas fa-handshake'
    },
    {
      key: 'hasMiniCor',
      name: 'Non-delegated',
      value: 'minicorrespondent',
      icon: 'fas fa-building'
    },
    {
      key: 'hasCor',
      name: 'Delegated',
      value: 'correspondent',
      icon: 'fas fa-university'
    }
  ])
  const channelList = ref<{ name: string; value: string; icon: string }[]>([])

  const setCurrentSelection = (selection: string) => {
    currentSelection.value = selection
    if (selection === 'minicorrespondent') {
      setIsNonDelPortal(true)
    } else if (selection === 'correspondent') {
      setIsCorPortal(true)
    } else if (selection === 'broker') {
      setIsBrokerPortal(true)
    }
  }

  const setIsFetchingPortalMode = (isFetching: boolean) => {
    isFetchingPortalMode.value = isFetching
  }

  const setAvailableChannels = (channels: CompanyPortalChannels) => {
    channelList.value = []
    availableChannels.value.forEach(channel => {
      if (channels[channel.key as keyof CompanyPortalChannels]) {
        channelList.value.push({
          name: channel.name,
          value: channel.value,
          icon: channel.icon
        })
      }
    })
    setHasMultiplePortalModes(channelList.value.length > 1)
  }

  const setHasMultiplePortalModes = (multiplePortalModes: boolean) => {
    hasMultiplePortalModes.value = multiplePortalModes
  }

  const setIsNonDelPortal = (isNonDel: boolean) => {
    isNonDelPortal.value = isNonDel
  }

  const setIsCorPortal = (isCor: boolean) => {
    isCorPortal.value = isCor
  }

  const setIsBrokerPortal = (isBroker: boolean) => {
    isBrokerPortal.value = isBroker
  }

  const fetchPortalMode = async () => {
    isFetchingPortalMode.value = true
    const response = await loanService.getPortalMode()
    if (response) {
      setCurrentSelection(response.currentSelection)
    }
    isFetchingPortalMode.value = false
  }

  const updatePortalMode = async (channel: string, hashedCompanyId: string, company: string) => {
    const response = await loanService.updatePortalMode({ channel, hashedCompanyId, company })
    setCurrentSelection(response.currentSelection)
  }

  const syncPortalModeWithCompany = async () => {
    const response = await portalModeService.syncPortalModeWithCompany()
    if (response?.data?.currentSelection) {
      setCurrentSelection(response.data.currentSelection)
    }
    if (response?.company) {
      setAvailableChannels(response.company)
    }
    return response
  }

  return {
    currentSelection,
    isFetchingPortalMode,
    hasMultiplePortalModes,
    availableChannels,
    channelList,
    isNonDelPortal,
    isCorPortal,
    isBrokerPortal,
    setCurrentSelection,
    setIsFetchingPortalMode,
    fetchPortalMode,
    updatePortalMode,
    syncPortalModeWithCompany,
    setHasMultiplePortalModes,
    setAvailableChannels,
    setIsNonDelPortal,
    setIsCorPortal,
    setIsBrokerPortal,
  }
})